import { useState, useEffect, useRef } from 'react';
import { X, Check, Trash2, Heart, Flame, Award, ChevronRight } from 'lucide-react';
import {
  subscribeToPendingPrayers,
  approvePrayer,
  rejectPrayer,
  deletePendingPrayer,
} from '@/services/prayers';
import { useToast } from '@/context/ToastContext';
import { PRAYER_CATEGORY_CONFIG } from '@/types/prayers';
import type { PendingPrayer, PrayerType } from '@/types';

interface PrayerApprovalQueueProps {
  onClose: () => void;
  userId: string;
  userName: string;
}

export function PrayerApprovalQueue({ onClose, userId, userName }: PrayerApprovalQueueProps) {
  const { showToast } = useToast();
  const [pendingPrayers, setPendingPrayers] = useState<PendingPrayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Subscribe to pending prayers
  useEffect(() => {
    const unsub = subscribeToPendingPrayers((prayers) => {
      setPendingPrayers(prayers);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const getIcon = (type: PrayerType) => {
    const config = PRAYER_CATEGORY_CONFIG[type];
    switch (type) {
      case 'healing':
        return <Heart size={14} className={config.iconColor} />;
      case 'memory':
        return <Flame size={14} className={config.iconColor} />;
      case 'dedication':
        return <Award size={14} className={config.iconColor} />;
    }
  };

  const handleApprove = async (prayer: PendingPrayer) => {
    setProcessingId(prayer.id);
    try {
      await approvePrayer(prayer.id, userId, userName);
      showToast({
        message: `Approved prayer for "${prayer.name}"`,
        type: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to approve prayer:', error);
      showToast({
        message: 'Failed to approve prayer',
        type: 'error',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (prayer: PendingPrayer) => {
    const reason = prompt('Reason for rejection (optional):');

    setProcessingId(prayer.id);
    try {
      await rejectPrayer(prayer.id, userId, userName, reason || undefined);
      showToast({
        message: `Rejected prayer for "${prayer.name}"`,
        type: 'warning',
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to reject prayer:', error);
      showToast({
        message: 'Failed to reject prayer',
        type: 'error',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (prayer: PendingPrayer) => {
    if (!confirm('Delete this prayer request permanently?')) return;

    setProcessingId(prayer.id);
    try {
      await deletePendingPrayer(prayer.id);
      showToast({
        message: 'Prayer request deleted',
        type: 'info',
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to delete prayer:', error);
      showToast({
        message: 'Failed to delete prayer',
        type: 'error',
      });
    } finally {
      setProcessingId(null);
    }
  };

  const contentWhenLoaded =
    pendingPrayers.length === 0 ? (
      <div className="p-6 text-center text-slate-400">
        <Heart size={32} strokeWidth={1} className="mx-auto mb-2 opacity-50" />
        <p className="text-sm">No pending prayer requests</p>
      </div>
    ) : (
      <div className="divide-y divide-slate-100">
        {pendingPrayers.map((prayer) => {
          const config = PRAYER_CATEGORY_CONFIG[prayer.type];
          const isProcessing = processingId === prayer.id;

          return (
            <div
              key={prayer.id}
              className={`p-3 hover:bg-slate-50 transition-colors ${isProcessing ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start gap-3">
                {/* Type Badge */}
                <div
                  className={`p-2 rounded-lg bg-gradient-to-r ${config.gradient} text-white shrink-0`}
                >
                  {getIcon(prayer.type)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800 truncate">{prayer.name}</span>
                    {prayer.hebrewName && (
                      <span className="text-slate-500 text-sm" dir="rtl">
                        {prayer.hebrewName}
                      </span>
                    )}
                  </div>
                  {prayer.note && (
                    <p className="text-xs text-slate-500 truncate mt-0.5">{prayer.note}</p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    by {prayer.addedByName || 'Unknown'} • {config.tabLabel}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleApprove(prayer)}
                    disabled={isProcessing}
                    className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Approve"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={() => handleReject(prayer)}
                    disabled={isProcessing}
                    className="p-1.5 text-orange-500 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Reject"
                  >
                    <X size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(prayer)}
                    disabled={isProcessing}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[90]" onClick={onClose} />

      {/* Dropdown - Fixed positioned and centered */}
      <div
        ref={menuRef}
        className="fixed left-1/2 -translate-x-1/2 top-[calc(var(--header-height)+var(--safe-area-top)+8px)] w-[calc(100%-2rem)] max-w-md bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-[100]"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2">
            <Heart size={18} className="text-[#8e4585]" />
            <h3 className="font-medium text-slate-800">Prayer Approval Queue</h3>
            {pendingPrayers.length > 0 && (
              <span className="px-2 py-0.5 bg-[#8e4585] text-white text-xs font-bold rounded-full">
                {pendingPrayers.length}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded">
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-slate-400">
              <div className="animate-pulse">Loading...</div>
            </div>
          ) : (
            contentWhenLoaded
          )}
        </div>

        {/* Footer - Link to full admin panel */}
        {pendingPrayers.length > 0 && (
          <a
            href="/admin/prayer-moderation"
            className="flex items-center justify-center gap-1 w-full p-3 border-t border-slate-100 text-sm text-[#8e4585] font-medium hover:bg-slate-50 transition-colors"
            onClick={onClose}
          >
            View all in admin panel
            <ChevronRight size={16} />
          </a>
        )}
      </div>
    </>
  );
}
